import { z } from 'zod';

import { getFeatureFlagsConfigAction } from '@kit/feature-flags/server';

export type { FeatureFlagsConfig } from '@kit/feature-flags';

type LanguagePriority = 'user' | 'application';

const FeatureFlagsSchema = z.object({
  enableThemeToggle: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_THEME_TOGGLE'
          : undefined,
    })
    .describe('Enable theme toggle in the user interface.'),
  enableAccountDeletion: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION'
          : undefined,
    })
    .describe('Enable personal account deletion.'),
  enableTeamDeletion: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION'
          : undefined,
    })
    .describe('Enable team deletion.'),
  enableTeamAccounts: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS'
          : undefined,
    })
    .describe('Enable team accounts.'),
  enableTeamCreation: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION'
          : undefined,
    })
    .describe('Enable team creation.'),
  enablePersonalAccountBilling: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING'
          : undefined,
    })
    .describe('Enable personal account billing.'),
  enableTeamAccountBilling: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING'
          : undefined,
    })
    .describe('Enable team account billing.'),
  languagePriority: z
    .enum(['user', 'application'], {
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_LANGUAGE_PRIORITY'
          : undefined,
    })
    .describe(
      `If set to user, use the user's preferred language. If set to application, use the application's default language.`,
    )
    .default('application'),
  enableNotifications: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_NOTIFICATIONS'
          : undefined,
    })
    .describe('Enable notifications functionality'),
  realtimeNotifications: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_REALTIME_NOTIFICATIONS'
          : undefined,
    })
    .describe('Enable realtime for the notifications functionality'),
  enableVersionUpdater: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_VERSION_UPDATER'
          : undefined,
    })
    .describe('Enable version updater'),
  enableSavedSignatures: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_SAVED_SIGNATURES'
          : undefined,
    })
    .describe('Enable saved signatures functionality'),
  enableCoaching: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_COACHING'
          : undefined,
    })
    .describe('Enable coaching pages and navigation'),
  enableAmbassadors: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_AMBASSADORS'
          : undefined,
    })
    .describe('Enable the brand-ambassadors module (pages + navigation)'),
  enableMarketplaceCommerce: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_MARKETPLACE_COMMERCE'
          : undefined,
    })
    .describe(
      'Enable Stripe Connect payments + commission on the marketplace. When false, the marketplace runs in free / contact-seller mode (buyers DM the seller via /home/inbox).',
    ),
});

/**
 * Static feature flags config from environment variables
 * Used as fallback during build time or when database is unavailable
 * @deprecated Use getFeatureFlagsConfig() for database-backed flags
 */
const featuresFlagConfig = FeatureFlagsSchema.parse({
  enableThemeToggle: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_THEME_TOGGLE,
    true,
  ),
  enableAccountDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION,
    true,
  ),
  enableTeamDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION,
    true,
  ),
  enableTeamAccounts: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS,
    false,
  ),
  enableTeamCreation: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION,
    false,
  ),
  enablePersonalAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING,
    false,
  ),
  enableTeamAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING,
    false,
  ),
  languagePriority: process.env
    .NEXT_PUBLIC_LANGUAGE_PRIORITY as LanguagePriority,
  enableNotifications: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_NOTIFICATIONS,
    true,
  ),
  realtimeNotifications: getBoolean(
    process.env.NEXT_PUBLIC_REALTIME_NOTIFICATIONS,
    true,
  ),
  enableVersionUpdater: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_VERSION_UPDATER,
    true,
  ),
  enableSavedSignatures: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_SAVED_SIGNATURES,
    true,
  ),
  enableCoaching: getBoolean(process.env.NEXT_PUBLIC_ENABLE_COACHING, false),
  // Disabled by default (2026-07-05): the module is gated off until the
  // brand-ambassadors flow is ready. Flip NEXT_PUBLIC_ENABLE_AMBASSADORS=true
  // to re-enable.
  enableAmbassadors: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_AMBASSADORS,
    false,
  ),
  enableMarketplaceCommerce: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_MARKETPLACE_COMMERCE,
    false,
  ),
} satisfies z.infer<typeof FeatureFlagsSchema>);

export default featuresFlagConfig;

/**
 * Fetch feature flags from database (async)
 * Use this in server components and server actions
 */
export async function getFeatureFlagsConfig() {
  return getFeatureFlagsConfigAction();
}

function getBoolean(value: unknown, defaultValue: boolean) {
  if (typeof value === 'string') {
    return value === 'true';
  }

  return defaultValue;
}
